import React, { Component } from 'react';
import { ArrowLeftIcon, ChevronRightIcon, ArchiveBoxIcon, ExclamationTriangleIcon, PencilIcon, InformationCircleIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import apiService from '../../services/apiService';
import outlookService from '../../services/outlookService';
import storageService from '../../services/storageService';
import authService from '../../services/authService';
import LoadingSpinner from '../common/LoadingSpinner';
import ShowHeadFields from './ShowHeadFields';
import RelatedRecords from './RelatedRecords';
import ConfirmationNotification from '../common/ConfirmationNotification';
import { renderIcon } from '../utils/Common';

class EntityDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      recordDetail: {},
      headerFields: [],
      allFields: [],
      showRelatedRecords: false,
      selectedRelation: null,
      showArchiveDialog: false,
      archiving: false,
      activeTab: 'details', // 'details' or 'related'
      showArchiveNotification: false,
      notificationMessage: '',
    };
  }

  componentDidMount() {
    // If we have entity_info with header fields, use it directly instead of API call
    if (this.props.entity_info && this.props.entity_info.header_fields) {
      
      this.setState({
        recordDetail: this.props.entity_info,
        headerFields: this.props.entity_info.header_fields,
        loading: false,
      });
      
      // Still load full record details for the Detail tab
      this.loadRecordDetails();
      return;
    }
    
    this.loadRecordDetails();
  }

  loadRecordDetails = async () => {
    const { crmid, onLogout } = this.props;
    
    
    // Only show loading if we don't have entity_info already
    if (!this.props.entity_info) {
      this.setState({ loading: true });
    }
    
    try {
      const result = await apiService.getRecordDetails(crmid);
      
      if (result.success) {
        const responseData = result.data;
        
        // Handle the API response structure: {details: recordData, header_fields: headerFieldsData}
        const recordDetail = responseData.details || responseData.recordDetail || {};
        const headerFields = responseData.header_fields || responseData.headerFields || [];
        const isEditable = responseData.editable || false;
        
        
        // Get all fields for the module to display in Detail tab
        const moduleFields = authService.getModuleFields(this.props.module || this.props.entity_info?.module);
        
        this.setState({
          recordDetail: { 
            ...recordDetail, 
            editable: isEditable,
            header_fields: headerFields,
            label: this.props.entity_info?.label || recordDetail.label
          },
          headerFields: headerFields,
          allFields: moduleFields,
          loading: false,
        });
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  handleRelatedTabClick = (tab) => {
    
    if (tab.type === 'ModComments') {
      if (this.props.onRelatedModuleInfo) {
        this.props.onRelatedModuleInfo('ModComments');
      }
    } else if (tab.type === 'Detail') {
      // Stay on current detail view - no action needed
    } else {
      // Handle all other related records including Emails and Calendar
      this.setState({
        showRelatedRecords: true,
        selectedRelation: {
          key: tab.type,
          label: tab.label,
          id: tab.id // Pass the id for relationid
        }
      });
    }
  };

  handleBackFromRelated = () => {
    this.setState({
      showRelatedRecords: false,
      selectedRelation: null
    });
  };

  handleEdit = (record) => {
    if (this.props.onEdit) {
      // Pass minimal edit info - let EntityForm handle the API call
      const editRecord = {
        editMode: true,
        crmid: this.props.crmid || this.props.entity_info?.crmid,
        module: this.props.entity_info?.module || this.props.module,
        label: this.props.entity_info?.label || this.state.recordDetail?.label
      };
      this.props.onEdit(editRecord);
    }
  };

  handleArchiveEmail = async () => {
    try {
      this.setState({ showArchiveDialog: false, archiving: true });

      const user = await storageService.getUser();

      // Validate Office.js context before proceeding
      if (!Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
        throw new Error('Office.js context not available. Please refresh the add-in.');
      }
      
      // Get complete email data including attachments, CC, BCC, etc.
      const completeEmailData = await outlookService.createCompleteEmailArchiveData(
        this.props.crmid,
        user.userid
      );

      const result = await apiService.archiveEmail(completeEmailData);

      // Check if recordid exists (either as string or object with recordid property)
      const recordId = result.data?.recordid;
      const hasValidRecordId = recordId && (
        (typeof recordId === 'string' && recordId.length > 0) ||
        (typeof recordId === 'object' && recordId.recordid)
      );

      if (result.success && hasValidRecordId) {
        const attachmentText = completeEmailData.hasAttachments
          ? ` with ${completeEmailData.attachments.length} attachment(s)`
          : '';

        this.setState({
          showArchiveNotification: true,
          notificationMessage: `Email archived successfully${attachmentText}`,
          archiving: false
        });
        return;
      } else if (result.needsLogout && this.props.onLogout) {
        this.props.onLogout();
        return;
      } else {
        this.setState({
          showArchiveNotification: true,
          notificationMessage: 'Failed to archive email. Please try again.',
          archiving: false
        });
        return;
      }
    } catch (error) {
      this.setState({
        showArchiveNotification: true,
        notificationMessage: 'Failed to archive email. Please try again.',
        archiving: false
      });
    } finally {
      this.setState({ archiving: false });
    }
  };

  handleArchiveNotificationComplete = () => {
    this.setState({ showArchiveNotification: false });
  };

  renderDetailFields = () => {
    const { recordDetail, allFields } = this.state;
    
    if (!allFields || allFields.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No field details available</p>
        </div>
      );
    }
    
    // Filter out system fields and group by sections if needed
    const displayFields = allFields.filter(field => {
      const fieldName = field.fieldname || field.name;
      return fieldName !== 'id' && 
             fieldName !== 'createdtime' && 
             fieldName !== 'modifiedtime' &&
             fieldName !== 'modifiedby' &&
             fieldName !== 'created_user_id';
    });
    
    return (
      <div className="space-y-4">
        {displayFields.map((field, index) => {
          const fieldName = field.fieldname || field.name;
          const fieldValue = recordDetail[fieldName];
          
          // Skip empty values
          if (!fieldValue || fieldValue === '' || fieldValue === '0') {
            return null;
          }
          
          // Handle object values from API
          let displayValue = fieldValue;
          if (typeof fieldValue === 'object' && fieldValue !== null) {
            displayValue = fieldValue.value || fieldValue.label || '';
          }
          
          return (
            <div key={index} className="flex flex-col space-y-1">
              <label className="text-sm font-semibold text-gray-700">
                {field.label}
              </label>
              <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                {displayValue || 'Not specified'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  render() {
    const {
      loading,
      recordDetail,
      headerFields,
      showRelatedRecords,
      selectedRelation,
      showArchiveDialog,
      archiving,
      activeTab,
      showArchiveNotification,
      notificationMessage
    } = this.state;
    const { onCancel, onBack, onRelatedModuleInfo, crmid, module } = this.props;

    if (loading) {
      return (
        <LoadingSpinner label="Loading record details..." variant="fullscreen" />
      );
    }

    // Show related records view
    if (showRelatedRecords && selectedRelation) {
      // Show Events component for Calendar
      if (selectedRelation.isEvents) {
        return (
          <Events
            recordId={crmid}
            relationId={selectedRelation.id || selectedRelation.key}
            onBack={this.handleBackFromRelated}
            onLogout={this.props.onLogout}
          />
        );
      }
      
      // Show RelatedRecords for other modules
      return (
        <RelatedRecords
          recordId={crmid}
          relationId={selectedRelation.id || selectedRelation.key} // Use id if available, fallback to key
          module={selectedRelation.key}
          relationLabel={selectedRelation.label}
          onBack={this.handleBackFromRelated}
          onLogout={this.props.onLogout}
        />
      );
    }

    return (
      <div className="space-y-4">
        {/* Success Notification */}
        <ConfirmationNotification
          show={showArchiveNotification}
          message={notificationMessage}
          type="archive"
          onComplete={this.handleArchiveNotificationComplete}
          duration={2500}
        />

        {/* Archive Email Confirmation Dialog */}
        <Transition appear show={showArchiveDialog} as={React.Fragment}>
          <Dialog as="div" className="relative z-10" onClose={() => this.setState({ showArchiveDialog: false })}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="ml-3">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Archive Email
                        </Dialog.Title>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to archive this email? This action will save the email and associate it with the current record.
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
                        onClick={() => this.setState({ showArchiveDialog: false })}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm"
                        style={{ backgroundColor: '#0075a8' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#005e86'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#0075a8'}
                        onClick={this.handleArchiveEmail}
                      >
                        Archive Email
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Loading Overlay */}
        {archiving && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-3"></div>
              <span className="text-sm text-gray-600 font-medium">Archiving email...</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="px-4 py-4 border-b border-gray-200">
            <div className="space-y-4">
              {/* Back Navigation */}
              {onBack ? (
                <button
                  onClick={onBack}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Back to Results
                </button>
              ) : (
                <button
                  onClick={onCancel}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Back
                </button>
              )}
              
              {/* Header Fields with Edit Icon */}
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <ShowHeadFields data={{
                    ...recordDetail,
                    label: this.props.entity_info?.label || recordDetail.label,
                    header_fields: this.props.entity_info?.header_fields || recordDetail.header_fields || headerFields
                  }} />
                </div>
                
                {/* Edit Icon */}
                <div className="ml-4 flex items-center space-x-2">
                  {(this.props.entity_info?.editable || recordDetail.editable) && (
                    <button
                      onClick={() => this.handleEdit(recordDetail)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit Record"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Email Action Button */}
              <div>
                <button
                  onClick={() => this.setState({ showArchiveDialog: true })}
                  className="w-full flex items-center justify-start px-3 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm text-left"
                  style={{ backgroundColor: '#339fcf' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0075a8'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#339fcf'}
                  disabled={archiving}
                >
                  {archiving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Archiving...
                    </>
                  ) : (
                    <>
                      <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                      Log Email to {this.props.entity_info?.label || recordDetail.label || 'Record'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {/* Tab Navigation */}
          <div className="px-4 pt-4">
            <div className="flex space-x-1 border-b border-gray-200">
              <button
                onClick={() => this.setState({ activeTab: 'details' })}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'details'
                    ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <InformationCircleIcon className="w-4 h-4 mr-2" />
                  Details
                </div>
              </button>
              <button
                onClick={() => this.setState({ activeTab: 'related' })}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'related'
                    ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <Squares2X2Icon className="w-4 h-4 mr-2" />
                  Related
                </div>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="px-4 pb-4">
            {activeTab === 'details' ? (
              <div className="pt-4">
                {/* All Record Fields */}
                {this.renderDetailFields()}
              </div>
            ) : (
              /* Related Tabs */
              <div className="pt-4">
                {this.props.relatedTabs && this.props.relatedTabs.length > 0 ? (
                  <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    {this.props.relatedTabs.map((tab, index) => (
                      <button
                        key={index}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left first:rounded-t-lg last:rounded-b-lg"
                        onClick={() => this.handleRelatedTabClick(tab)}
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                            {renderIcon(tab.type)}
                          </div>
                          <span className="ml-2 text-sm font-medium text-gray-900">{tab.label}</span>
                        </div>
                        <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Squares2X2Icon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Related Information</h3>
                    <p className="text-sm text-gray-500">
                      No related tabs available for this record.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    );
  }
}

export default EntityDetail;